import apiClient from './apiClient';

/**
 * Employee service for handling employee-related API calls
 */
const employeeService = {
  /**
   * Get all employees with optional filters
   * @param {Object} params - Query parameters (filters, pagination)
   * @returns {Promise} API response with employees and pagination
   */
  getEmployees: async (params = {}) => {
    const response = await apiClient.get('/employees', { params });
    return response.data;
  },

  /**
   * Get employee directory — lightweight names + IDs for dropdowns and assignee lists.
   * Safe for all roles (employees.view permission, not employees.view_all).
   * @param {Object} params - { status }
   * @returns {Promise} { employees: [{ id, firstName, lastName, email, jobTitle, employmentStatus }] }
   */
  getDirectory: async (params = {}) => {
    const response = await apiClient.get('/employees/directory', { params });
    return response.data;
  },

  /**
   * Get employee statistics for dashboard
   * @returns {Promise} API response with employee statistics
   */
  getStatistics: async () => {
    const response = await apiClient.get('/employees/statistics');
    return response.data;
  },

  /**
   * Get single employee by ID
   * @param {string} employeeId - Employee ID
   * @returns {Promise} API response with employee data
   */
  getEmployee: async (employeeId) => {
    const response = await apiClient.get(`/employees/${employeeId}`);
    return response.data;
  },

  /**
   * Create new employee
   * @param {Object} employeeData - Employee information
   * @returns {Promise} API response with created employee
   */
  createEmployee: async (employeeData) => {
    const response = await apiClient.post('/employees', employeeData);
    return response.data;
  },

  /**
   * Update existing employee
   * @param {string} employeeId - Employee ID
   * @param {Object} updateData - Updated employee information
   * @returns {Promise} API response with updated employee
   */
  updateEmployee: async (employeeId, updateData) => {
    const response = await apiClient.put(`/employees/${employeeId}`, updateData);
    return response.data;
  },

  /**
   * Change employee status
   * @param {string} employeeId - Employee ID
   * @param {string} status - New employment status (active, inactive, terminated)
   * @param {string|null} reason - Reason for status change (required for termination)
   * @returns {Promise} API response with updated employee
   */
  changeStatus: async (employeeId, status, reason = null) => {
    const response = await apiClient.put(`/employees/${employeeId}/status`, {
      status,
      reason
    });
    return response.data;
  },

  /**
   * Get employment status options for forms
   * @returns {Promise} API response with status options
   */
  getEmploymentStatuses: async () => {
    const response = await apiClient.get('/employees/options/employment-statuses');
    return response.data;
  },

  /**
   * Get employment type options for forms
   * @returns {Promise} API response with type options
   */
  getEmploymentTypes: async () => {
    const response = await apiClient.get('/employees/options/employment-types');
    return response.data;
  },

  /**
   * Get pay classification options for forms (hourly, salaried, contractor)
   * @returns {Promise} API response with classification options
   */
  getPayClassifications: async () => {
    const response = await apiClient.get('/employees/options/pay-classifications');
    return response.data;
  },

  /**
   * Get work arrangement options for forms (on_site, remote, hybrid)
   * @returns {Promise} API response with arrangement options
   */
  getWorkArrangements: async () => {
    const response = await apiClient.get('/employees/options/work-arrangements');
    return response.data;
  },

  /**
   * Get employee's assigned locations
   * @param {string} employeeId - Employee ID
   * @returns {Promise} API response with location assignments
   */
  getEmployeeLocations: async (employeeId) => {
    const response = await apiClient.get(`/employees/${employeeId}/locations`);
    return response.data;
  },

  /**
   * Update employee's location assignments
   * @param {string} employeeId - Employee ID
   * @param {Object} locationData - { locationIds: [], primaryLocationId: string }
   * @returns {Promise} API response with updated assignments
   */
  updateEmployeeLocations: async (employeeId, locationData) => {
    const response = await apiClient.put(`/employees/${employeeId}/locations`, locationData);
    return response.data;
  },

  /**
   * Get employee's resolved work state for labor law compliance
   * @param {string} employeeId - Employee ID
   * @param {string} [locationId] - Optional shift location
   * @returns {Promise} API response with work state info
   */
  getEmployeeWorkState: async (employeeId, locationId = null) => {
    const params = locationId ? { locationId } : {};
    const response = await apiClient.get(`/employees/${employeeId}/work-state`, { params });
    return response.data;
  },

  /**
   * Resend Wristband invitation email for a pending/expired employee invite
   * @param {string} employeeId - Employee ID
   * @returns {Promise} API response with success message and email
   */
  resendInvite: async (employeeId) => {
    const response = await apiClient.post(`/employees/${employeeId}/resend-invite`);
    return response.data;
  }
};

export default employeeService;